/**
 * ChatContent — real text-chat thread, web port of `apps/WunderGuide/components/ChatLog.tsx`
 * + `useMessages` hook. Wired to the same backend endpoints as RN:
 *   GET  /chat/v1/{sessionId}/messages
 *   POST /chat/v1/{sessionId}/message
 *
 * If no chat session is selected yet, prompt the user to start a voice session
 * first (selectedSessionId is set when useAudioChat bootstrapSession runs).
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import {
  bootstrapChatSession,
  getMergedHistory,
  streamChatMessage,
  type ChatHistoryItem,
  type ChatMessageItem,
  type SendChatMessageResponse,
} from '../../api/chatApi';
import { useChatSession } from '../../context/ChatSessionContext';
import { useSocketEvent } from '../../context/SocketContext';
import Spinner from '../../common/Spinner';
import './ChatContent.css';

type Sender = 'user' | 'assistant' | 'system';

interface ThreadMessage {
  id: string;
  sender: Sender;
  content: ChatMessageItem[];
  at: string;
  pending?: boolean;
  failed?: boolean;
}

interface ChatContentProps {
  /**
   * Drawer visibility. The DrawerHost keeps content mounted (so draft text
   * + scroll position survive close/reopen), but we gate network effects
   * on this so we don't poll `/chat/sessions` every 2s while the user is on
   * Dashboard or any other drawer.
   */
  visible?: boolean;
}

const ChatContent: React.FC<ChatContentProps> = ({ visible = true }) => {
  const { selectedSessionId, setSelectedSessionId, agent, setAgent } =
    useChatSession();
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<ThreadMessage[]>([]);
  messagesRef.current = messages;

  // Auto-bootstrap a chat session when drawer first opens, mirroring RN's
  // implicit session creation. The thread should never be MIA. Persona
  // (avatar + display name) comes from the API on the same response.
  // Gated on `visible` because Drawer keeps children mounted — without this
  // gate the bootstrap (and below polling) ran even on Dashboard.
  useEffect(() => {
    if (!visible) return;
    if (selectedSessionId) return;
    let cancelled = false;
    (async () => {
      const result = await bootstrapChatSession();
      if (cancelled || !result) return;
      setSelectedSessionId(result.id);
      if (result.agent) setAgent(result.agent);
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, selectedSessionId, setSelectedSessionId, setAgent]);

  // Merge real (server) messages over the existing thread without duplicating
  // any optimistic entries with matching role+text within ±5min (generous
  // window absorbs device/server clock skew that was triggering false
  // survivors and visible duplicates).
  const mergeServerMessages = useCallback((server: ThreadMessage[]) => {
    setMessages(prev => {
      const optimistic = prev.filter(
        m =>
          m.id.startsWith('socket-') ||
          m.id.startsWith('optimistic-') ||
          m.pending,
      );
      const survivingOptimistic = optimistic.filter(om => {
        const omText = String(om.content[0]?.text ?? '').trim();
        if (!omText) return true;
        const omTime = parseUtcTime(om.at);
        return !server.some(sm => {
          if (sm.sender !== om.sender) return false;
          const smText = String(sm.content[0]?.text ?? '').trim();
          if (smText !== omText) return false;
          const smTime = parseUtcTime(sm.at);
          if (Number.isNaN(omTime) || Number.isNaN(smTime)) return true;
          return Math.abs(smTime - omTime) <= 300_000;
        });
      });
      const sortedServer = [...server].sort(
        (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
      );
      return [...sortedServer, ...survivingOptimistic];
    });
  }, []);

  // Load merged voice + text history (RN's `/functions/v1/chat` endpoint)
  // when the session changes, then keep refreshing every 2s to pick up live
  // voice transcripts mid-conversation. Mirrors `useMessages.ts` polling.
  // Gated on `visible` so the 2s loop only runs while the drawer is open.
  useEffect(() => {
    if (!visible) return;
    if (!selectedSessionId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const load = async (initial: boolean) => {
      if (initial) setLoading(true);
      try {
        const data = await getMergedHistory(selectedSessionId, 50, 0);
        if (cancelled) return;
        const fromHistory: ThreadMessage[] = data.messages.map(
          (m: ChatHistoryItem) => ({
            id: m.id,
            sender: m.role,
            content: m.content,
            at: m.created_at,
          }),
        );
        if (initial) {
          setMessages(fromHistory);
        } else {
          mergeServerMessages(fromHistory);
        }
        setError(null);
      } catch (err: any) {
        if (cancelled) return;
        if (initial && err?.response?.status !== 404) {
          setError('Failed to load history.');
        }
      } finally {
        if (initial && !cancelled) setLoading(false);
      }
    };

    load(true);
    timer = setInterval(() => load(false), 2000);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [visible, selectedSessionId, mergeServerMessages]);

  // Live transcript via Socket.IO — RN's useAudioChat emits 'partial' /
  // 'final' / 'assistant' events while voice is active. Piggyback on those
  // for free streaming text in the chat thread.
  useSocketEvent<{ text: string; sessionId?: string }>('final', d => {
    if (!d?.text) return;
    setMessages(prev => [
      ...prev,
      {
        id: `socket-user-${Date.now()}`,
        sender: 'user',
        content: [{ type: 'text', text: d.text }],
        at: new Date().toISOString(),
      },
    ]);
  });
  useSocketEvent<{ text: string; sessionId?: string }>('assistant', d => {
    if (!d?.text) return;
    setMessages(prev => [
      ...prev,
      {
        id: `socket-bot-${Date.now()}`,
        sender: 'assistant',
        content: [{ type: 'text', text: d.text }],
        at: new Date().toISOString(),
      },
    ]);
  });

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text || !selectedSessionId || sending) return;

    const ts = Date.now();
    const userMsgId = `optimistic-user-${ts}`;
    const streamingMsgId = `optimistic-streaming-${ts}`;
    const userMsg: ThreadMessage = {
      id: userMsgId,
      sender: 'user',
      content: [{ type: 'text', text }],
      at: new Date().toISOString(),
      pending: true,
    };
    // The streaming bubble starts as a typing indicator; the first delta
    // swaps it to text in-place. `done` finalizes it (cards + interaction_id).
    const streamingMsg: ThreadMessage = {
      id: streamingMsgId,
      sender: 'assistant',
      content: [{ type: 'loading', text: 'Thinking…' }],
      at: new Date().toISOString(),
      pending: true,
    };
    setMessages(prev => [...prev, userMsg, streamingMsg]);
    setDraft('');
    setSending(true);

    const history = messagesRef.current
      .slice(-10)
      .filter(m => m.content[0]?.type === 'text')
      .map(m => ({
        role: (m.sender === 'assistant' ? 'assistant' : 'user') as
          | 'user'
          | 'assistant',
        content: String(m.content[0]?.text ?? ''),
      }));

    // Boxed in an object so TS narrowing in the closures below doesn't
    // collapse the outer-scope refs to `never` after the await.
    const state: {
      accumulated: string;
      finalPayload: SendChatMessageResponse | null;
      streamErr: Error | null;
    } = { accumulated: '', finalPayload: null, streamErr: null };

    try {
      await streamChatMessage(
        selectedSessionId,
        text,
        {
          onDelta(delta) {
            state.accumulated += delta;
            const current = state.accumulated;
            // flushSync forces React to commit + paint synchronously
            // before this callback returns. Without it, React 18 batches
            // every onDelta's setMessages into one late render — the
            // user only sees the final text, not the streaming animation.
            flushSync(() => {
              setMessages(prev =>
                prev.map(m =>
                  m.id === streamingMsgId
                    ? { ...m, content: [{ type: 'text', text: current }] }
                    : m,
                ),
              );
            });
          },
          onDone(payload) {
            state.finalPayload = payload;
          },
          onError(err) {
            state.streamErr = err;
          },
        },
        { history, language: 'en-US' },
      );
    } catch (err) {
      state.streamErr = err instanceof Error ? err : new Error(String(err));
    }

    if (state.streamErr && !state.finalPayload && !state.accumulated) {
      setMessages(prev =>
        prev
          .filter(m => m.id !== streamingMsgId)
          .map(m => (m.id === userMsgId ? { ...m, failed: true } : m)),
      );
      setError('Failed to send.');
      setSending(false);
      return;
    }

    const responseText = state.finalPayload?.response ?? state.accumulated;
    const responseContent: ChatMessageItem[] = [];
    if (responseText)
      responseContent.push({ type: 'text', text: responseText });
    for (const item of state.finalPayload?.content ?? [])
      responseContent.push(item);

    // Finalize the streaming bubble IN PLACE rather than removing it and
    // appending a separate asst_optimistic. The race: server logs the
    // assistant row before flushing SSE done, so a 2s poll can land between
    // server-log and client-done. That merge drops streamingMsg (its text
    // matches the now-present server row) and brings in asst_real. If we
    // then concat asst_optimistic, state has BOTH — a visible duplicate
    // that won't dedup on subsequent polls if server text and client text
    // disagree byte-for-byte. In-place update means: streamingMsg becomes
    // the final bubble (still optimistic-prefixed → dedups next poll), or
    // if it's already gone, asst_real is already there — nothing to add.
    setMessages(prev => {
      const hasStreaming = prev.some(m => m.id === streamingMsgId);
      const withUserUnpending = prev.map(m =>
        m.id === userMsgId ? { ...m, pending: false } : m,
      );
      if (!hasStreaming) return withUserUnpending;
      return withUserUnpending.map(m =>
        m.id === streamingMsgId
          ? { ...m, pending: false, content: responseContent }
          : m,
      );
    });

    setSending(false);
  }, [draft, selectedSessionId, sending]);

  // Autoscroll to newest.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const grouped = useMemo(() => {
    return messages.map((m, i) => {
      const prev = messages[i - 1];
      const showStamp = !prev || formatStamp(prev.at) !== formatStamp(m.at);
      return { msg: m, showStamp };
    });
  }, [messages]);

  const personaName = agent?.displayName || 'Assistant';
  const personaAvatar = agent?.avatarUrl;

  return (
    <div className="wg-chat">
      <div className="wg-chat__persona">
        <div className="wg-chat__persona-avatar">
          {personaAvatar ? (
            <img src={personaAvatar} alt={personaName} />
          ) : (
            <span className="wg-chat__persona-fallback">
              {personaName.slice(0, 1)}
            </span>
          )}
        </div>
        <span className="wg-chat__persona-name">{personaName}</span>
      </div>

      {!selectedSessionId && (
        <div className="wg-chat__empty">
          <p>Starting your chat…</p>
        </div>
      )}

      {selectedSessionId && (
        <>
          <div ref={scrollRef} className="wg-chat__thread">
            {loading && messages.length === 0 && (
              <div className="wg-chat__center">
                <Spinner size="large" />
              </div>
            )}
            {!loading && messages.length === 0 && (
              <div className="wg-chat__center">
                Say hi to Leilani — she’ll plan your day.
              </div>
            )}
            {grouped.map(({ msg, showStamp }) => (
              <React.Fragment key={msg.id}>
                {showStamp && (
                  <div className="wg-chat__stamp">{formatStamp(msg.at)}</div>
                )}
                <MessageBubble message={msg} />
              </React.Fragment>
            ))}
          </div>

          <form
            className="wg-chat__input"
            onSubmit={e => {
              e.preventDefault();
              submit();
            }}
          >
            <input
              type="text"
              placeholder="Type a message…"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              disabled={sending}
            />
            <button
              type="submit"
              className="wg-chat__send"
              aria-label="Send"
              disabled={!draft.trim() || sending}
            >
              <SendIcon />
            </button>
          </form>
          {error && <div className="wg-chat__error">{error}</div>}
        </>
      )}
    </div>
  );
};

export default ChatContent;

const MessageBubble: React.FC<{ message: ThreadMessage }> = ({ message }) => {
  const isBot = message.sender === 'assistant';
  return (
    <div
      className={`wg-chat__msg wg-chat__msg--${isBot ? 'bot' : 'user'}${
        message.failed ? ' is-failed' : ''
      }`}
      aria-live={isBot ? 'polite' : undefined}
    >
      {isBot && (
        <span className="wg-chat__msg-mark" aria-hidden>
          <SparkleGlyph />
        </span>
      )}
      <span className="wg-chat__msg-bubble">
        {message.content.map((c, i) => renderItem(c, i))}
      </span>
    </div>
  );
};

function renderItem(item: ChatMessageItem, i: number): React.ReactNode {
  if (item.type === 'text' || item.type === 'system') {
    return <span key={i}>{item.text}</span>;
  }
  if (item.type === 'loading') {
    return (
      <span key={i} className="wg-chat__typing" aria-hidden>
        <span /> <span /> <span />
      </span>
    );
  }
  if (item.type === 'card') {
    return (
      <span key={i} className="wg-chat__card-inline">
        {(item.title as string | undefined) || 'Card'}
      </span>
    );
  }
  // Other rich types (image, map, actions, web) fall back to text label.
  return null;
}

// Server timestamps from log_interaction_sync arrive without a UTC suffix
// ("2026-05-03T06:07:18.430357"). JS parses such strings as the user's local
// zone — silently shifting every server message by the user's UTC offset.
// That breaks (a) the displayed chat stamps and (b) the dedup time window
// inside mergeServerMessages, which compares against optimistic ISO-with-Z.
function parseUtcTime(iso: string): number {
  if (!iso) return NaN;
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const normalized = iso.includes(' ') ? iso.replace(' ', 'T') : iso;
  return new Date(hasZone ? normalized : `${normalized}Z`).getTime();
}

function formatStamp(iso: string): string {
  try {
    const ms = parseUtcTime(iso);
    if (Number.isNaN(ms)) return iso;
    return new Date(ms).toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function SparkleGlyph() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 3l1.6 5.4L19 10l-5.4 1.6L12 17l-1.6-5.4L5 10l5.4-1.6z" />
    </svg>
  );
}
function SendIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
